import * as React from 'react'
import { useState } from 'react'
import {
    useDataProvider,
    useNotify,
    Button
} from 'react-admin'
import {
    Box,
    Card,
    CardContent,
    Typography,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { User } from '@/types'

interface UserConsentExportProps {
    userId: number
    user?: User
}

export default function UserConsentExport({ userId, user }: UserConsentExportProps) {
    const [exportDialogOpen, setExportDialogOpen] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [exportData, setExportData] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const dataProvider = useDataProvider()
    const notify = useNotify()

    const handleExportData = async () => {
        try {
            setLoading(true)
            const result = await dataProvider.getOne(`users/${userId}/consents/export`, { id: userId })
            setExportData(result.data)
            setExportDialogOpen(true)
        } catch (error: any) {
            notify(error?.body?.error || 'Erreur lors de l\'export des données', { type: 'error' })
        } finally {
            setLoading(false)
        }
    }

    const handleDownloadExport = () => {
        if (!exportData) return

        const jsonString = JSON.stringify(exportData, null, 2)
        const blob = new Blob([jsonString], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `user-${userId}-consent-data-${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        notify('Export téléchargé avec succès', { type: 'success' })
        setExportDialogOpen(false)
    }

    const handleDeleteData = async () => {
        try {
            setLoading(true)
            await dataProvider.delete(`users/${userId}/consents/delete`, {
                id: userId,
                previousData: { id: userId },
                meta: { confirm: true }
            })
            notify('Données de consentement supprimées avec succès', { type: 'success' })
            setDeleteDialogOpen(false)
        } catch (error: any) {
            notify(error?.body?.error || 'Erreur lors de la suppression des données', { type: 'error' })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Gestion des données RGPD
            </Typography>

            <Card>
                <CardContent>
                    <Stack spacing={3}>
                        {/* Export des données */}
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Export des données de consentement
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Téléchargez toutes les données de consentement de cet utilisateur au format JSON.
                                Inclut l'historique complet des actions et métadonnées.
                            </Typography>
                            <Button
                                label="Exporter les données"
                                onClick={handleExportData}
                                disabled={loading}
                                variant="outlined"
                            >
                                <DownloadIcon sx={{ mr: 1 }} />
                            </Button>
                        </Box>

                        {/* Zone dangereuse */}
                        <Alert severity="warning">
                            <Typography variant="subtitle2" gutterBottom>
                                Zone dangereuse
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                La suppression des données de consentement est <strong>irréversible</strong>.
                                Cette action supprimera définitivement tous les consentements et l'historique 
                                associé à cet utilisateur.
                            </Typography>
                            <Button
                                label="Supprimer toutes les données"
                                onClick={() => setDeleteDialogOpen(true)}
                                disabled={loading}
                                color="error"
                                variant="outlined"
                            >
                                <DeleteForeverIcon sx={{ mr: 1 }} />
                            </Button>
                        </Alert>
                    </Stack>
                </CardContent>
            </Card>

            {/* Dialog Export */}
            <Dialog 
                open={exportDialogOpen} 
                onClose={() => setExportDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Export des données de consentement
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Données exportées pour {user ? `${user.firstName} ${user.lastName}` : `l'utilisateur ID ${userId}`}
                    </Typography>
                    
                    {exportData && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Aperçu des données :
                            </Typography>
                            <Box component="pre" sx={{
                                backgroundColor: 'action.hover',
                                p: 2,
                                borderRadius: 1,
                                overflow: 'auto',
                                maxHeight: 400,
                                fontSize: '0.8rem'
                            }}>
                                {JSON.stringify(exportData, null, 2)}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        label="Annuler"
                        onClick={() => setExportDialogOpen(false)}
                        variant="text"
                    />
                    <Button
                        label="Télécharger"
                        onClick={handleDownloadExport}
                        variant="contained"
                        disabled={!exportData}
                    />
                </DialogActions>
            </Dialog>

            {/* Dialog Suppression */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Confirmer la suppression
                </DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body1">
                            <strong>Attention !</strong> Cette action est irréversible.
                        </Typography>
                    </Alert>
                    <Typography variant="body2">
                        Vous êtes sur le point de supprimer définitivement toutes les données 
                        de consentement pour {user ? `${user.firstName} ${user.lastName}` : `l'utilisateur ID ${userId}`}.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Cette action supprimera :
                    </Typography>
                    <ul>
                        <li>Tous les consentements actifs et révoqués</li>
                        <li>L'historique complet des actions</li>
                        <li>Toutes les métadonnées associées</li>
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button
                        label="Annuler"
                        onClick={() => setDeleteDialogOpen(false)}
                        variant="text"
                    />
                    <Button
                        label="Confirmer la suppression"
                        onClick={handleDeleteData}
                        color="error"
                        disabled={loading}
                    />
                </DialogActions>
            </Dialog>
        </Box>
    )
}